import React, { useState, useEffect, useRef } from 'react';
import {
  getAllAdminChats,
  getAdminChatMessages,
  sendMessageToNutritionist,
  markAsRead,
  subscribeToAllAdminChats,
  subscribeToAdminChatMessages,
} from '../services/adminChatService';
import './AdminChat.css';

export default function AdminChat() {
  const [chats, setChats] = useState([]);
  const [selectedChat, setSelectedChat] = useState(null);
  const [messages, setMessages] = useState([]);
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef(null);

  // Carregar lista de conversas
  useEffect(() => {
    let unsubscribe = null;

    async function loadChats() {
      setLoading(true);
      try {
        const chatsData = await getAllAdminChats();
        setChats(chatsData);

        // Configurar listener em tempo real
        unsubscribe = subscribeToAllAdminChats((newChats) => {
          setChats(newChats);
        });
      } catch (error) {
        console.error('Erro ao carregar conversas:', error);
      } finally {
        setLoading(false);
      }
    }

    loadChats();

    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, []);

  // Carregar mensagens quando selecionar uma conversa
  useEffect(() => {
    let unsubscribe = null;

    if (!selectedChat) {
      setMessages([]);
      return;
    }

    async function loadMessages() {
      try {
        const messagesData = await getAdminChatMessages(selectedChat.nutritionistId);
        setMessages(messagesData);

        // Marcar como lida
        await markAsRead(selectedChat.nutritionistId);

        // Configurar listener em tempo real
        unsubscribe = subscribeToAdminChatMessages(selectedChat.nutritionistId, (newMessages) => {
          setMessages(newMessages);
          // Marcar como lida quando receber nova mensagem
          markAsRead(selectedChat.nutritionistId);
        });
      } catch (error) {
        console.error('Erro ao carregar mensagens:', error);
      }
    }

    loadMessages();

    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, [selectedChat]);

  // Scroll automático para última mensagem
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  const handleSelectChat = (chat) => {
    setSelectedChat(chat);
    setMessage('');
  };

  const handleSend = async () => {
    if (!selectedChat || !message.trim() || sending) return;

    try {
      setSending(true);
      await sendMessageToNutritionist(selectedChat.nutritionistId, message);
      setMessage('');
    } catch (error) {
      console.error('Erro ao enviar mensagem:', error);
      alert('Erro ao enviar mensagem. Tente novamente.');
    } finally {
      setSending(false);
    }
  };

  const totalUnread = chats.reduce((sum, chat) => sum + (chat.unreadCount || 0), 0);

  if (loading) {
    return (
      <div className="admin-chat-container">
        <div className="admin-chat-loading">Carregando conversas...</div>
      </div>
    );
  }

  return (
    <div className="admin-chat-container">
      <div className="admin-chat-header">
        <h2>Suporte - Chat com Nutricionistas</h2>
        {totalUnread > 0 && (
          <span className="admin-chat-unread-badge">{totalUnread}</span>
        )}
      </div>

      <div className="admin-chat-layout">
        {/* Lista de conversas */}
        <div className="admin-chat-list-container">
          <h3>Conversas ({chats.length})</h3>
          {chats.length === 0 ? (
            <div className="admin-chat-empty-list">
              <p>Nenhuma conversa ainda.</p>
            </div>
          ) : (
            <ul className="admin-chat-list">
              {chats.map((chat) => (
                <li
                  key={chat.nutritionistId}
                  className={`admin-chat-list-item ${
                    selectedChat?.nutritionistId === chat.nutritionistId ? 'active' : ''
                  }`}
                  onClick={() => handleSelectChat(chat)}
                >
                  <div className="admin-chat-list-item-header">
                    <strong>{chat.nutritionistName || 'Nutricionista'}</strong>
                    {chat.unreadCount > 0 && (
                      <span className="admin-chat-item-badge">{chat.unreadCount}</span>
                    )}
                  </div>
                  <div className="admin-chat-list-item-email">{chat.nutritionistEmail}</div>
                  {chat.lastMessage && (
                    <div className="admin-chat-list-item-preview">{chat.lastMessage}</div>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Área de chat */}
        <div className="admin-chat-window-container">
          {selectedChat ? (
            <>
              <div className="admin-chat-window-header">
                <div>
                  <h3>{selectedChat.nutritionistName || 'Nutricionista'}</h3>
                  <p>{selectedChat.nutritionistEmail}</p>
                </div>
              </div>

              <div className="admin-chat-messages">
                {messages.length === 0 ? (
                  <div className="admin-chat-empty-messages">
                    <p>Nenhuma mensagem ainda. Inicie a conversa!</p>
                  </div>
                ) : (
                  messages.map((m) => (
                    <div
                      key={m.id}
                      className={`admin-chat-message ${
                        m.author === 'admin' ? 'sent' : 'received'
                      }`}
                    >
                      {m.image ? (
                        <div style={{ marginBottom: '8px' }}>
                          <img
                            src={m.image}
                            alt="Imagem enviada"
                            style={{
                              maxWidth: '300px',
                              maxHeight: '300px',
                              borderRadius: '8px',
                              objectFit: 'cover',
                            }}
                          />
                        </div>
                      ) : m.audio ? (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <audio controls style={{ maxWidth: '250px' }}>
                            <source src={m.audio} type="audio/webm" />
                            <source src={m.audio} type="audio/mpeg" />
                            <source src={m.audio} type="audio/wav" />
                            Seu navegador não suporta áudio.
                          </audio>
                          <span style={{ fontSize: '12px', opacity: 0.7 }}>🎤</span>
                        </div>
                      ) : (
                        <p>{m.text}</p>
                      )}
                      {m.createdAt && (
                        <span className="admin-chat-time">{m.createdAt}</span>
                      )}
                    </div>
                  ))
                )}
                <div ref={messagesEndRef} />
              </div>

              <div className="admin-chat-input">
                <input
                  type="text"
                  className="admin-chat-text-input"
                  placeholder="Digite uma mensagem..."
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      handleSend();
                    }
                  }}
                  disabled={sending}
                />
                <button
                  className="btn btn-primary"
                  onClick={handleSend}
                  disabled={!message.trim() || sending}
                >
                  {sending ? 'Enviando...' : 'Enviar'}
                </button>
              </div>
            </>
          ) : (
            <div className="admin-chat-empty-window">
              <p>Selecione uma conversa para visualizar as mensagens.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}



